// CLI v0.8 — `grid secrets unset [<name>] KEY1 KEY2 ...`. Spec v17 §13.9.
// Confirmation prompt fires by default per audit Part B.4 #5.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';
import { existsSync } from 'fs';
import { join } from 'path';

interface PatchResponse {
  status: string;
  keys_set: string[];
  keys_unset: string[];
  restarting: boolean;
}

export interface RunSecretsUnsetOpts {
  client: IV2Client;
  name: string | undefined;
  keys: string[];
  yes?: boolean;
  cwd?: string;
  isTTY?: boolean;
  promptFn?: (message: string) => Promise<boolean>;
}

export async function runSecretsUnset(opts: RunSecretsUnsetOpts): Promise<void> {
  if (opts.keys.length === 0) {
    throw new V2ApiError(400, 'VALIDATION_FAILED', 'Expected at least one key to unset.');
  }
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);

  // QA-11 — `secrets set <name> KEY=val` honors an explicit leading
  // entity name even inside a linked folder (the `=` disambiguates name from
  // pair). `unset` keys carry no `=`, so the in-folder splitter treats every
  // positional as a key — meaning `secrets unset <name> KEY` from inside that
  // entity's own directory swallowed `<name>` as a bogus key and reported
  // "Unset 0 secrets". When no explicit name was extracted (link path) and the
  // first positional is the resolved entity's own slug/id, drop it so the
  // command behaves like its `set` counterpart.
  let keys = opts.keys;
  if (
    opts.name === undefined &&
    keys.length > 1 &&
    (keys[0] === target.name || keys[0] === target.entity_id)
  ) {
    keys = keys.slice(1);
  }

  if (!opts.yes) {
    const isTTY = opts.isTTY ?? Boolean(process.stdin.isTTY);
    if (!isTTY) {
      throw new V2ApiError(
        400, 'CONFIRMATION_REQUIRED',
        'Unset needs confirmation. Re-run with --yes in non-interactive shells.',
      );
    }
    const n = keys.length;
    const ok = await (opts.promptFn ?? defaultConfirm)(
      `Unset ${n} secret${n === 1 ? '' : 's'} on ${target.name}?`,
    );
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  const resp = await opts.client.patch<PatchResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/secrets`,
    { unset: keys },
  );
  const n = resp.keys_unset.length;
  process.stdout.write(`Unset ${n} secret${n === 1 ? '' : 's'}.\n`);
  if (resp.restarting) process.stdout.write('Rolling restart dispatched.\n');
}

export function splitNameAndKeys(
  args: string[],
  cwd: string,
): { name: string | undefined; keys: string[] } {
  const inFolder = existsSync(join(cwd, 'cloudgrid.yaml')) || existsSync(join(cwd, '.cloudgrid', 'link.json'));
  if (inFolder) return { name: undefined, keys: args };
  if (args.length < 2) return { name: undefined, keys: args };
  return { name: args[0], keys: args.slice(1) };
}

async function defaultConfirm(message: string): Promise<boolean> {
  const { ok } = await inquirer.prompt([
    { type: 'confirm', name: 'ok', message, default: false },
  ]);
  return Boolean(ok);
}

export const secretsUnsetCommand: CommanderCommand = new Command('unset')
  .description(
    'Unset secrets on a runtime (admin only). ' +
    'In-folder: grid secrets unset KEY1 KEY2. ' +
    'Out-of-folder: grid secrets unset <name> KEY1 KEY2.',
  )
  .argument('<args...>', 'Keys to unset, optionally preceded by an entity name')
  .option('-y, --yes', 'Skip the confirmation prompt')
  .action(async (args: string[], opts: { yes?: boolean }) => {
    try {
      const { name, keys } = splitNameAndKeys(args, process.cwd());
      const client = await getV2Client();
      await runSecretsUnset({ client, name, keys, yes: opts.yes });
    } catch (err) {
      await handleV2Error(err);
    }
  });
